import React from 'react'
import { Input, Table, Empty, Button, Avatar, Typography, Divider, Tabs } from 'antd'
import { SearchOutlined, UserOutlined, EditOutlined } from '@ant-design/icons'
import AddPatient from '../../../../Admin/system-users/components/AddPatient'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { TabPane } = Tabs

const PatientSelection = ({
  patient,
  setPatient,
  searchResults,
  searching,
  searchQuery,
  setSearchQuery,
  handlePatientQuery,
  activePatientTab,
  setActivePatientTab,
  setService,
  token
}) => {
  // Helper function to calculate age from date of birth
  const calculateAge = (patient) => {
    // Debug log

    // Check different possible locations for dateOfBirth
    let dateOfBirth = patient.dateOfBirth || patient.patientRecord?.dateOfBirth

    if (dateOfBirth) {
      // Debug log
      const age = dayjs().diff(dayjs(dateOfBirth), 'year')
      // Debug log
      return age
    }

    // Debug log

    // Fallback to ID number calculation (South African format)
    if (patient.idNo && patient.idNo.length >= 6) {
      const yearPrefix = patient.idNo.substring(0, 2)
      const month = patient.idNo.substring(2, 4)
      const day = patient.idNo.substring(4, 6)

      // Determine century (00-21 = 2000s, 22-99 = 1900s)
      const year = parseInt(yearPrefix) <= 21 ? `20${yearPrefix}` : `19${yearPrefix}`
      const birthDate = dayjs(`${year}-${month}-${day}`)

      if (birthDate.isValid()) {
        const age = dayjs().diff(birthDate, 'year')
        // Debug log
        return age
      }
    }

    // Debug log
    return null
  }

  // Helper function to get gender
  const getGender = (patient) => {
    // Debug log

    // Check different possible locations for gender
    let gender = patient.gender || patient.patientRecord?.gender

    if (gender) {
      // Debug log
      return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
    }

    // Debug log

    // Fallback to ID number calculation (South African format)
    if (patient.idNo && patient.idNo.length >= 10) {
      // South African ID format: YYMMDDSSSSAAZ (7th-10th digits indicate gender)
      const genderDigit = parseInt(patient.idNo.substring(6, 10))
      const genderFromId = genderDigit < 5000 ? 'Female' : 'Male'
      // Debug log
      return genderFromId
    }

    // Debug log
    return null
  }

  const renderPatientCard = () => {
    if (!patient) {
      return (
        <>
          <Input
            prefix={<SearchOutlined style={{ color: token.colorPrimary }} />}
            placeholder='Search by name or ID'
            style={{ marginTop: 12, marginBottom: 12, borderRadius: 40, padding: '6px 8px' }}
            onChange={(e) => handlePatientQuery(e.target.value)}
          />
          {searchQuery && (
            <div className='patient-table-wrapper' style={{ marginBottom: 8 }}>
              {searchResults.length === 0 && !searching ? (
                <Empty
                  description='No patients found'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '16px 0' }}
                />
              ) : (
                <Table
                  size='small'
                  rowClassName={() => 'patient-row-hover'}
                  onRow={(record) => ({
                    onClick: () => {
                      setSearchQuery('')
                      setPatient(record)
                    }
                  })}
                  loading={searching}
                  dataSource={searchResults}
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'firstName',
                      key: 'firstName',
                      render: (text) => <span>{text}</span>,
                      width: '30%',
                      className: 'patient-table-cell'
                    },
                    {
                      title: 'Surname',
                      dataIndex: 'lastName',
                      key: 'lastName',
                      render: (text) => <span>{text}</span>,
                      width: '35%',
                      className: 'patient-table-cell'
                    },
                    {
                      title: 'ID/Passport',
                      dataIndex: 'idNo',
                      key: 'idNo',
                      render: (text) => <span>{text || '-'}</span>,
                      width: '35%',
                      className: 'patient-table-cell'
                    }
                  ]}
                  pagination={{
                    pageSize: 5,
                    size: 'small',
                    showSizeChanger: false,
                    itemRender: (current, type, originalElement) => {
                      if (type === 'prev') {
                        return <span style={{ fontSize: 16, color: token.colorPrimary }}>&lt;</span>
                      }
                      if (type === 'next') {
                        return <span style={{ fontSize: 16, color: token.colorPrimary }}>&gt;</span>
                      }
                      return originalElement
                    }
                  }}
                />
              )}
            </div>
          )}
        </>
      )
    }

    const age = calculateAge(patient)
    const gender = getGender(patient)

    return (
      <div
        className='selected-patient-card'
        style={{
          padding: '12px',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #f0f0f0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar
            size={40}
            icon={<UserOutlined style={{ color: token.colorPrimary }} />}
            style={{ backgroundColor: 'rgba(0,0,0,0.05)', flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Title level={5} style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                {patient.firstName} {patient.lastName}
              </Title>
              {age && (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  {age}y
                </Text>
              )}
              {gender && (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  {gender}
                </Text>
              )}
            </div>
            {patient.idNo && (
              <Text type='secondary' style={{ fontSize: '12px', color: '#8c8c8c' }}>
                ID: {patient.idNo}
              </Text>
            )}
          </div>
          <Button
            type='primary'
            icon={<EditOutlined style={{ color: 'white' }} />}
            onClick={() => {
              setPatient(null)
              setService(null)
              setSearchQuery('')
              setActivePatientTab('search')
            }}
            style={{
              height: '36px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              flexShrink: 0
            }}
            size='small'
          >
            Change
          </Button>
        </div>
      </div>
    )
  }

  if (patient) {
    return renderPatientCard()
  }

  return (
    <Tabs defaultActiveKey='search' size='small' activeKey={activePatientTab} onChange={setActivePatientTab}>
      <TabPane
        tab={
          <span>
            <SearchOutlined style={{ color: token.colorPrimary }} /> Search Patient
          </span>
        }
        key='search'
      >
        {renderPatientCard()}
      </TabPane>
      <TabPane
        tab={
          <span>
            <UserOutlined style={{ color: token.colorPrimary }} /> Add New Patient
          </span>
        }
        key='add'
      >
        <AddPatient
          isBooking
          setBookingPatient={setPatient}
          setAddingForBooking={() => setActivePatientTab('search')}
          resetFunction={() => setActivePatientTab('search')}
        />
      </TabPane>
    </Tabs>
  )
}

export default PatientSelection
